import React from 'react';

/**
 * Shared design tokens + utility components for the resume templates.
 *
 * Goals:
 *   1. ATS-friendliness: every template renders linear text top-to-bottom in a
 *      single semantic flow, even if the visual layout looks columned.
 *   2. Print-ready: fixed page width, page-break-inside hints on sections,
 *      colors flagged with print-color-adjust so accents survive PDF export.
 *   3. Consistent design language across templates so users can swap templates
 *      without their content shifting awkwardly.
 *
 * Conventions used by every template:
 *   - 8.5x11" page (816px @ 96dpi). Min-height set so empty resumes don't look short.
 *   - Body font sizes in pt-equivalent rem (so PDF export keeps print sizes).
 *   - Each <Section> wraps in a `print-avoid-break` style so a section doesn't
 *     get awkwardly split across pages.
 */

// =====================================================================
// Page wrapper (consistent paper size across templates)
// =====================================================================
export const Page = ({ children, accent = '#1f2937', background = '#ffffff', textColor = '#111827', fontFamily, padding = '0.75in', style = {} }) => (
  <div
    style={{
      width: '8.5in',
      minHeight: '11in',
      maxWidth: '100%',
      margin: '0 auto',
      padding,
      backgroundColor: background,
      color: textColor,
      fontFamily,
      lineHeight: 1.45,
      fontSize: '10.5pt',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      boxSizing: 'border-box',
      ...style
    }}
    data-accent={accent}
  >
    {children}
  </div>
);

// =====================================================================
// Helpers — all bullet/skill parsing happens here so templates stay simple
// =====================================================================

// Split a freeform string into "blocks" separated by blank lines.
// Each block is treated as one experience/education entry.
export const splitBlocks = (text) => {
  if (!text || typeof text !== 'string') return [];
  return text
    .split(/\n\s*\n/)
    .map(s => s.trim())
    .filter(Boolean);
};

// Within a block, split into lines and detect bullet markers.
// Returns { headline, bullets } where headline is the first non-bullet line and
// bullets is the remaining lines stripped of "•" / "-" / "*" prefixes.
export const splitEntry = (block) => {
  const lines = block.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return { headline: '', bullets: [] };
  const isBullet = (l) => /^[•\-*]\s+/.test(l);
  // First non-bullet line becomes the headline (role/company); rest are bullets/body.
  const firstNonBulletIdx = lines.findIndex(l => !isBullet(l));
  if (firstNonBulletIdx === -1) {
    // All bullets, no headline — fine, render as bullet list under empty header.
    return { headline: '', bullets: lines.map(l => l.replace(/^[•\-*]\s+/, '')) };
  }
  const headline = lines[firstNonBulletIdx];
  const rest = [...lines.slice(0, firstNonBulletIdx), ...lines.slice(firstNonBulletIdx + 1)];
  const bullets = rest.map(l => l.replace(/^[•\-*]\s+/, ''));
  return { headline, bullets };
};

// Parse a comma/pipe/semicolon-separated skills string into an array of trimmed strings.
export const splitSkills = (text) => {
  if (!text || typeof text !== 'string') return [];
  return text
    .split(/[,;|\n]/)
    .map(s => s.trim())
    .filter(Boolean);
};

// Split education / certifications / languages on newlines into individual lines.
export const splitLines = (text) => {
  if (!text || typeof text !== 'string') return [];
  return text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
};

// Pretty-print a URL (strip leading https:// / www.).
export const prettyLink = (url) => {
  if (!url) return '';
  return String(url).replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
};

// =====================================================================
// Reusable section primitives
// =====================================================================

export const Section = ({ children, style = {} }) => (
  <section
    style={{
      marginBottom: '0.5rem',
      pageBreakInside: 'avoid',
      breakInside: 'avoid-page',
      ...style
    }}
  >
    {children}
  </section>
);

// Single underline section title (most templates).
export const SectionTitleUnderlined = ({ children, accent = '#1f2937', size = '11pt', uppercase = true, letterSpacing = '0.08em' }) => (
  <h2
    style={{
      fontSize: size,
      fontWeight: 700,
      color: accent,
      borderBottom: `1.5px solid ${accent}`,
      paddingBottom: '2px',
      marginBottom: '0.3rem',
      textTransform: uppercase ? 'uppercase' : 'none',
      letterSpacing
    }}
  >
    {children}
  </h2>
);

// Variant: just the label, no border — for sidebar usage.
export const SectionTitleBare = ({ children, accent = '#1f2937', size = '10pt', letterSpacing = '0.1em' }) => (
  <h2
    style={{
      fontSize: size,
      fontWeight: 700,
      color: accent,
      textTransform: 'uppercase',
      letterSpacing,
      marginBottom: '0.35rem'
    }}
  >
    {children}
  </h2>
);

// =====================================================================
// Skills rendered as pills (modern look) or comma list (compact look).
// =====================================================================

export const SkillPills = ({ skills, accent = '#1f2937', background = '#f3f4f6' }) => {
  const items = Array.isArray(skills) ? skills : splitSkills(skills);
  if (items.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {items.map((s, i) => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            padding: '2px 9px',
            borderRadius: '999px',
            backgroundColor: background,
            color: accent,
            fontSize: '9.5pt',
            border: '1px solid rgba(0,0,0,0.06)',
            whiteSpace: 'nowrap'
          }}
        >
          {s}
        </span>
      ))}
    </div>
  );
};

export const SkillsInline = ({ skills, separator = ' • ' }) => {
  const items = Array.isArray(skills) ? skills : splitSkills(skills);
  if (items.length === 0) return null;
  return <p style={{ margin: 0 }}>{items.join(separator)}</p>;
};

// =====================================================================
// Experience rendered as a list of role blocks.
// =====================================================================

export const ExperienceList = ({ text, accent = '#1f2937', headerStyle }) => {
  const blocks = splitBlocks(text);
  if (blocks.length === 0) return null;
  return (
    <div>
      {blocks.map((block, i) => {
        const { headline, bullets } = splitEntry(block);
        return (
          <div
            key={i}
            style={{
              marginBottom: '0.55rem',
              pageBreakInside: 'avoid',
              breakInside: 'avoid-page'
            }}
          >
            {headline && (
              <div
                style={{
                  fontWeight: 600,
                  color: accent,
                  fontSize: '10.5pt',
                  marginBottom: '2px',
                  ...headerStyle
                }}
              >
                {headline}
              </div>
            )}
            {bullets.length > 0 && (
              <ul style={{ margin: '0 0 0 1rem', padding: 0, listStyleType: 'disc' }}>
                {bullets.map((b, j) => (
                  <li key={j} style={{ marginBottom: '1px' }}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

// =====================================================================
// Generic block list — for education / certifications / languages.
// Renders newline-separated entries one per line.
// =====================================================================

export const LineList = ({ text, gap = '2px' }) => {
  const lines = splitLines(text);
  if (lines.length === 0) return null;
  return (
    <div>
      {lines.map((l, i) => (
        <div key={i} style={{ marginBottom: gap }}>{l}</div>
      ))}
    </div>
  );
};

// =====================================================================
// Contact line — used in headers
// =====================================================================

export const ContactRow = ({ data, separator = ' • ', style = {} }) => {
  const parts = [
    data.email,
    data.phone,
    data.linkedin && prettyLink(data.linkedin),
    data.portfolio && prettyLink(data.portfolio)
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <p style={{ margin: 0, fontSize: '9.5pt', color: '#4b5563', ...style }}>
      {parts.join(separator)}
    </p>
  );
};
